// admin/pages/AdminAnalytics.jsx - COMPLETE FILE
// Cycle 6 Phase 2: Enhanced with Token Health, Tier Analytics, Platform Accountability tabs
// Updated: January 3, 2026

import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import {
  TrendingUp,
  Users,
  DollarSign,
  ShoppingCart,
  Activity,
  ArrowUp,
  ArrowDown,
  Award,
  Target,
  RefreshCw,
  Package,
  Coins,
  PieChart,
  Shield,
  BarChart3,
  Crown,
  Medal,
  Star,
  Zap,
  Heart,
  CheckCircle,
  Clock,
  UserCheck
} from 'lucide-react';

// ============================================
// GRAPHQL QUERIES
// ============================================

const GET_ANALYTICS_DATA = gql`
  query GetAnalyticsData($days: Int) {
    platformMetrics {
      totalUsers
      totalGroups
      activeGroups
      totalPurchases
      totalSavings
      totalRevenue
      newUsers
      totalTokens
      avgTokenBalance
      avgGroupSize
      avgSavingsPerUser
      totalProducts
      availableProducts
    }
    revenueAnalytics(days: $days) {
      date
      revenue
      purchases
      savings
    }
    categoryAnalytics {
      category
      productCount
      avgPrice
    }
    topGroups(limit: 5) {
      id
      name
      memberCount
      totalSavings
      totalPurchases
    }
  }
`;

const GET_TOKEN_HEALTH = gql`
  query GetTokenHealth($days: Int) {
    tokenHealthMetrics(days: $days) {
      totalTokensInCirculation
      totalTokensEarned
      totalTokensRedeemed
      tokensEarnedThisPeriod
      tokensRedeemedThisPeriod
      earnRate
      redeemRate
      velocityRatio
      avgBalancePerUser
      medianBalance
      usersWithTokens
      usersWithZeroBalance
      topHolders {
        userId
        displayName
        balance
        tier
      }
      circulationTrend {
        date
        value
      }
      earnVsRedeemTrend {
        date
        earned
        redeemed
      }
    }
  }
`;

const GET_TIER_ANALYTICS = gql`
  query GetTierAnalytics {
    tierAnalytics {
      tierDistribution {
        tier
        count
        percentage
        color
      }
      totalMembers
      progressionStats {
        promotionsThisMonth
        demotionsThisMonth
        avgDaysToSilver
        avgDaysToGold
        avgDaysToPlatinum
      }
      spendingByTier {
        tier
        avgMonthlySpend
        avgOrderValue
        totalSpend
        memberCount
      }
      engagementByTier {
        tier
        avgJobsCompleted
        avgGroupsJoined
        avgTokensEarned
        avgLoginFrequency
      }
    }
  }
`;

const GET_PLATFORM_ACCOUNTABILITY = gql`
  query GetPlatformAccountability {
    platformAccountability {
      totalFeedbackReceived
      avgRating
      feedbackByCategory {
        category
        count
        avgRating
      }
      platformTrustScore
      uptimePercentage
      avgResponseTime
      totalSavingsDelivered
      totalJobsCreated
      totalJobsCompleted
      jobCompletionRate
      avgDeliveryTime
      activeUsersThisWeek
      activeUsersThisMonth
      communityGrowthRate
    }
  }
`;

const GET_USER_GROWTH = gql`
  query GetUserGrowth($days: Int) {
    userGrowth(days: $days) {
      date
      newUsers
      cumulativeUsers
    }
  }
`;

// ============================================
// MAIN COMPONENT
// ============================================

const AdminAnalytics = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState(30);
  
  // Fetch all analytics data
  const { loading: overviewLoading, data: overviewData, refetch: refetchOverview } = useQuery(GET_ANALYTICS_DATA, {
    variables: { days: timeRange },
    pollInterval: 60000
  });

  const { loading: tokenLoading, data: tokenData, refetch: refetchToken } = useQuery(GET_TOKEN_HEALTH, {
    variables: { days: timeRange },
    skip: activeTab !== 'tokens',
    pollInterval: 60000
  });

  const { loading: tierLoading, data: tierData, refetch: refetchTier } = useQuery(GET_TIER_ANALYTICS, {
    skip: activeTab !== 'tiers',
    pollInterval: 60000
  });

  const { loading: accountabilityLoading, data: accountabilityData, refetch: refetchAccountability } = useQuery(GET_PLATFORM_ACCOUNTABILITY, {
    skip: activeTab !== 'accountability',
    pollInterval: 60000
  });

  const { loading: growthLoading, data: growthData } = useQuery(GET_USER_GROWTH, {
    variables: { days: timeRange },
    skip: activeTab !== 'growth',
    pollInterval: 60000
  });

  const handleRefresh = () => {
    refetchOverview();
    if (activeTab === 'tokens') refetchToken();
    if (activeTab === 'tiers') refetchTier();
    if (activeTab === 'accountability') refetchAccountability();
  };

  const metrics = overviewData?.platformMetrics || {};
  const tokenHealth = tokenData?.tokenHealthMetrics || {};
  const tierAnalytics = tierData?.tierAnalytics || {};
  const accountability = accountabilityData?.platformAccountability || {};
  const userGrowth = growthData?.userGrowth || [];

  // ============================================
  // REUSABLE COMPONENTS
  // ============================================

  const StatCard = ({ title, value, icon: Icon, color = 'blue', prefix = '', suffix = '', subtext = '' }) => (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 bg-${color}-50 rounded-lg`}>
          <Icon size={20} className={`text-${color}-600`} />
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900">
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value || 0}{suffix}
      </div>
      <div className="text-sm text-gray-600">{title}</div>
      {subtext && <div className="text-xs text-gray-400 mt-1">{subtext}</div>}
    </div>
  );

  const SimpleBarChart = ({ data, dataKey, labelKey, height = 200, color = 'blue', maxBars = 14 }) => {
    if (!data || data.length === 0) {
      return <div className="text-center text-gray-500 py-8">No data available</div>;
    }
    
    const displayData = data.slice(-maxBars);
    const maxValue = Math.max(...displayData.map(d => d[dataKey] || 0));
    const minValue = Math.min(...displayData.map(d => d[dataKey] || 0));
    const barAreaHeight = height - 50;
    
    return (
      <div className="relative overflow-hidden" style={{ height }}>
        <div className="flex items-end gap-1" style={{ height: barAreaHeight }}>
          {displayData.map((item, index) => {
            const value = item[dataKey] || 0;
            // Normalize: smallest bar is 20% height, largest is 100%
            const normalizedHeight = maxValue === minValue 
              ? 50 
              : 20 + ((value - minValue) / (maxValue - minValue)) * 80;
            
            return (
              <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" style={{ maxWidth: '40px' }}>
                <div className="text-xs text-gray-600 mb-1" style={{ fontSize: '10px' }}>
                  {value > 0 ? value.toFixed(0) : ''}
                </div>
                <div 
                  className="w-full rounded-t"
                  style={{ 
                    height: `${normalizedHeight}%`,
                    minHeight: '4px',
                    backgroundColor: color === 'yellow' ? '#EAB308' : color === 'green' ? '#22C55E' : '#3B82F6'
                  }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between gap-1 mt-1">
          {displayData.map((item, index) => (
            <div key={index} className="flex-1 text-center text-xs text-gray-500" style={{ maxWidth: '40px', fontSize: '9px' }}>
              {item[labelKey]?.slice(-5) || ''}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const DualBarChart = ({ data, key1, key2, labelKey, height = 200, maxBars = 14 }) => {
    if (!data || data.length === 0) {
      return <div className="text-center text-gray-500 py-8">No data available</div>;
    }
    
    // Limit to most recent maxBars entries
    const displayData = data.slice(-maxBars);
    const maxValue = Math.max(
      ...displayData.map(d => Math.max(d[key1] || 0, d[key2] || 0))
    );
    
    return (
      <div className="relative" style={{ height }}>
        <div className="flex items-end justify-between h-full gap-2">
          {displayData.map((item, index) => (
            <div key={index} className="flex-1 flex flex-col items-center justify-end" style={{ maxWidth: '50px' }}>
              <div className="flex gap-0.5 items-end w-full justify-center" style={{ height: height - 30 }}>
                <div 
                  className="w-2 bg-green-500 rounded-t"
                  style={{ 
                    height: maxValue > 0 ? `${Math.max(((item[key1] || 0) / maxValue) * 100, 2)}%` : '2px',
                    minHeight: '4px'
                  }}
                  title={`Earned: ${item[key1] || 0}`}
                />
                <div 
                  className="w-2 bg-red-400 rounded-t"
                  style={{ 
                    height: maxValue > 0 ? `${Math.max(((item[key2] || 0) / maxValue) * 100, 2)}%` : '2px',
                    minHeight: '4px'
                  }}
                  title={`Redeemed: ${item[key2] || 0}`}
                />
              </div>
              <div className="text-xs mt-1 text-gray-500" style={{ fontSize: '9px' }}>
                {item[labelKey]?.slice(-5) || ''}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-4 mt-2 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 bg-green-500 rounded"></span> Earned
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 bg-red-400 rounded"></span> Redeemed
          </span>
        </div>
      </div>
    );
  };

  const TierBadge = ({ tier }) => {
    const config = {
      NEW_USER: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Users },
      BRONZE: { bg: 'bg-orange-100', text: 'text-orange-800', icon: Medal },
      SILVER: { bg: 'bg-gray-100', text: 'text-gray-800', icon: Medal },
      GOLD: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Crown },
      PLATINUM: { bg: 'bg-purple-100', text: 'text-purple-800', icon: Star }
    };
    const { bg, text, icon: TierIcon } = config[tier] || config.NEW_USER;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${bg} ${text}`}>
        <TierIcon size={12} />
        {tier === 'NEW_USER' ? 'NEW' : tier}
      </span>
    );
  };

  const ProgressBar = ({ value, max = 100, color = 'blue' }) => (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div 
        className={`bg-${color}-500 h-2 rounded-full transition-all duration-500`}
        style={{ width: `${Math.min((value / max) * 100, 100)}%` }}
      />
    </div>
  );

  // ============================================
  // TAB CONTENT COMPONENTS
  // ============================================

  const OverviewTab = () => (
    <>
      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Users" value={metrics.totalUsers} icon={Users} color="blue" />
        <StatCard title="Active Groups" value={metrics.activeGroups} icon={Activity} color="green" />
        <StatCard title="Total Savings" value={metrics.totalSavings?.toFixed(2)} icon={TrendingUp} color="yellow" prefix="$" />
        <StatCard title="Total Revenue" value={metrics.totalRevenue?.toFixed(2)} icon={DollarSign} color="purple" prefix="$" />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="text-sm text-gray-600">Avg Savings/User</div>
          <div className="text-xl font-bold">${(metrics.avgSavingsPerUser || 0).toFixed(2)}</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="text-sm text-gray-600">Avg Group Size</div>
          <div className="text-xl font-bold">{metrics.avgGroupSize || 0} members</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="text-sm text-gray-600">Active Products</div>
          <div className="text-xl font-bold">{metrics.availableProducts || 0}</div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border">
          <div className="text-sm text-gray-600">New Users</div>
          <div className="text-xl font-bold">{metrics.newUsers || 0}</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Revenue Trend</h3>
          <SimpleBarChart data={overviewData?.revenueAnalytics || []} dataKey="revenue" labelKey="date" />
        </div>
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">Products by Category</h3>
          <SimpleBarChart data={overviewData?.categoryAnalytics || []} dataKey="productCount" labelKey="category" color="green" />
        </div>
      </div>

      {/* Top Groups Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Top Performing Groups</h3>
        </div>
        <div className="p-6">
          {(overviewData?.topGroups || []).length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="text-xs text-gray-500 uppercase">
                  <th className="text-left pb-3">Group Name</th>
                  <th className="text-center pb-3">Members</th>
                  <th className="text-center pb-3">Total Savings</th>
                  <th className="text-center pb-3">Purchases</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(overviewData?.topGroups || []).map((group) => (
                  <tr key={group.id} className="text-sm">
                    <td className="py-3">{group.name}</td>
                    <td className="text-center py-3">{group.memberCount}</td>
                    <td className="text-center py-3">${(group.totalSavings || 0).toFixed(2)}</td>
                    <td className="text-center py-3">{group.totalPurchases}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center text-gray-500 py-8">No group data available yet</div>
          )}
        </div>
      </div>
    </>
  );

  const TokenHealthTab = () => {
    if (tokenLoading) {
      return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
    }

    return (
      <>
        {/* Token Key Metrics */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatCard 
            title="Tokens in Circulation" 
            value={Math.round(tokenHealth.totalTokensInCirculation || 0)} 
            icon={Coins} 
            color="yellow"
            subtext={`$${((tokenHealth.totalTokensInCirculation || 0) * 0.10).toFixed(2)} value`}
          />
          <StatCard 
            title="Earn Rate" 
            value={(tokenHealth.earnRate || 0).toFixed(1)} 
            icon={TrendingUp} 
            color="green"
            suffix="/day"
          />
          <StatCard 
            title="Redeem Rate" 
            value={(tokenHealth.redeemRate || 0).toFixed(1)} 
            icon={ShoppingCart} 
            color="red"
            suffix="/day"
          />
          <StatCard 
            title="Velocity Ratio" 
            value={(tokenHealth.velocityRatio || 0).toFixed(2)} 
            icon={Zap} 
            color="purple"
            subtext="Redeemed ÷ Earned"
          />
        </div>

        {/* Distribution Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="text-sm text-gray-600">Users with Tokens</div>
            <div className="text-xl font-bold text-green-600">{tokenHealth.usersWithTokens || 0}</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="text-sm text-gray-600">Users with Zero</div>
            <div className="text-xl font-bold text-gray-400">{tokenHealth.usersWithZeroBalance || 0}</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="text-sm text-gray-600">Avg Balance</div>
            <div className="text-xl font-bold">{(tokenHealth.avgBalancePerUser || 0).toFixed(0)} tokens</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="text-sm text-gray-600">Median Balance</div>
            <div className="text-xl font-bold">{(tokenHealth.medianBalance || 0).toFixed(0)} tokens</div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border overflow-hidden">
            <h3 className="text-lg font-semibold mb-4">Token Circulation Over Time</h3>
            <SimpleBarChart 
              data={tokenHealth.circulationTrend || []} 
              dataKey="value" 
              labelKey="date" 
              color="yellow"
            />
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Earn vs Redeem (Daily)</h3>
            <DualBarChart 
              data={tokenHealth.earnVsRedeemTrend || []} 
              key1="earned" 
              key2="redeemed" 
              labelKey="date"
            />
          </div>
        </div>

        {/* Top Holders */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Top Token Holders</h3>
          </div>
          <div className="p-6">
            {(tokenHealth.topHolders || []).length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase">
                    <th className="text-left pb-3">User</th>
                    <th className="text-center pb-3">Tier</th>
                    <th className="text-right pb-3">Balance</th>
                    <th className="text-right pb-3">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(tokenHealth.topHolders || []).map((holder, idx) => (
                    <tr key={idx} className="text-sm">
                      <td className="py-3">{holder.displayName}</td>
                      <td className="text-center py-3"><TierBadge tier={holder.tier} /></td>
                      <td className="text-right py-3 font-medium">{holder.balance.toLocaleString()}</td>
                      <td className="text-right py-3 text-green-600">${(holder.balance * 0.10).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center text-gray-500 py-8">No token holders yet</div>
            )}
          </div>
        </div>
      </>
    );
  };

  const TierAnalyticsTab = () => {
    if (tierLoading) {
      return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
    }

    const tiers = tierAnalytics.tierDistribution || [];
    const spending = tierAnalytics.spendingByTier || [];
    const engagement = tierAnalytics.engagementByTier || [];
    const progression = tierAnalytics.progressionStats || {};

    return (
      <>
        {/* Tier Distribution */}
        <div className="bg-white rounded-lg p-6 shadow-sm border mb-6">
          <h3 className="text-lg font-semibold mb-4">Tier Distribution</h3>
          <div className="grid grid-cols-4 gap-4">
            {tiers.map((tier) => (
              <div key={tier.tier} className="text-center">
                <div 
                  className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-2"
                  style={{ backgroundColor: tier.color + '20', border: `3px solid ${tier.color}` }}
                >
                  <div>
                    <div className="text-2xl font-bold" style={{ color: tier.color }}>{tier.count}</div>
                    <div className="text-xs text-gray-600">{tier.percentage}%</div>
                  </div>
                </div>
                <TierBadge tier={tier.tier} />
              </div>
            ))}
          </div>
          <div className="mt-4 text-center text-sm text-gray-600">
            Total Members: <span className="font-semibold">{tierAnalytics.totalMembers || 0}</span>
          </div>
        </div>

        {/* Progression Stats */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border text-center">
            <ArrowUp className="mx-auto text-green-500 mb-2" size={24} />
            <div className="text-xl font-bold text-green-600">{progression.promotionsThisMonth || 0}</div>
            <div className="text-xs text-gray-600">Promotions This Month</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border text-center">
            <ArrowDown className="mx-auto text-red-400 mb-2" size={24} />
            <div className="text-xl font-bold text-red-500">{progression.demotionsThisMonth || 0}</div>
            <div className="text-xs text-gray-600">Demotions This Month</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border text-center">
            <Clock className="mx-auto text-gray-400 mb-2" size={24} />
            <div className="text-xl font-bold">{progression.avgDaysToSilver || 0}</div>
            <div className="text-xs text-gray-600">Avg Days to Silver</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border text-center">
            <Clock className="mx-auto text-yellow-500 mb-2" size={24} />
            <div className="text-xl font-bold">{progression.avgDaysToGold || 0}</div>
            <div className="text-xs text-gray-600">Avg Days to Gold</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border text-center">
            <Clock className="mx-auto text-purple-500 mb-2" size={24} />
            <div className="text-xl font-bold">{progression.avgDaysToPlatinum || 0}</div>
            <div className="text-xs text-gray-600">Avg Days to Platinum</div>
          </div>
        </div>

        {/* Spending by Tier */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">Spending by Tier</h3>
            </div>
            <div className="p-6">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase">
                    <th className="text-left pb-3">Tier</th>
                    <th className="text-right pb-3">Members</th>
                    <th className="text-right pb-3">Avg Order</th>
                    <th className="text-right pb-3">Total Spend</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {spending.map((s) => (
                    <tr key={s.tier} className="text-sm">
                      <td className="py-3"><TierBadge tier={s.tier} /></td>
                      <td className="text-right py-3">{s.memberCount}</td>
                      <td className="text-right py-3">${(s.avgOrderValue || 0).toFixed(2)}</td>
                      <td className="text-right py-3 font-medium">${(s.totalSpend || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Engagement by Tier */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">Engagement by Tier</h3>
            </div>
            <div className="p-6">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase">
                    <th className="text-left pb-3">Tier</th>
                    <th className="text-right pb-3">Avg Jobs</th>
                    <th className="text-right pb-3">Avg Tokens</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {engagement.map((e) => (
                    <tr key={e.tier} className="text-sm">
                      <td className="py-3"><TierBadge tier={e.tier} /></td>
                      <td className="text-right py-3">{e.avgJobsCompleted}</td>
                      <td className="text-right py-3">{e.avgTokensEarned}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    );
  };

  const PlatformAccountabilityTab = () => {
    if (accountabilityLoading) {
      return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
    }

    return (
      <>
        {/* Trust Score Hero */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Platform Trust Score</h2>
              <p className="text-blue-100">Composite metric based on job completion, user activity, and savings delivery</p>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold">{(accountability.platformTrustScore || 0).toFixed(1)}</div>
              <div className="text-blue-200">out of 5.0</div>
              <div className="flex justify-center mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star} 
                    size={20} 
                    className={star <= Math.round(accountability.platformTrustScore || 0) ? 'text-yellow-300 fill-yellow-300' : 'text-blue-300'} 
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatCard 
            title="Total Savings Delivered" 
            value={(accountability.totalSavingsDelivered || 0).toFixed(2)} 
            icon={DollarSign} 
            color="green"
            prefix="$"
          />
          <StatCard 
            title="Job Completion Rate" 
            value={accountability.jobCompletionRate || 0} 
            icon={CheckCircle} 
            color="blue"
            suffix="%"
          />
          <StatCard 
            title="Active Users (Week)" 
            value={accountability.activeUsersThisWeek || 0} 
            icon={UserCheck} 
            color="purple"
          />
          <StatCard 
            title="Community Growth" 
            value={accountability.communityGrowthRate || 0} 
            icon={TrendingUp} 
            color="yellow"
            suffix="%"
            subtext="vs last month"
          />
        </div>

        {/* Job Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Jobs Created</h3>
              <Package className="text-blue-500" size={24} />
            </div>
            <div className="text-3xl font-bold">{accountability.totalJobsCreated || 0}</div>
            <div className="text-sm text-gray-500">Total platform jobs</div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Jobs Completed</h3>
              <CheckCircle className="text-green-500" size={24} />
            </div>
            <div className="text-3xl font-bold text-green-600">{accountability.totalJobsCompleted || 0}</div>
            <div className="text-sm text-gray-500">Successfully finished</div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Completion Rate</h3>
              <Target className="text-purple-500" size={24} />
            </div>
            <div className="text-3xl font-bold text-purple-600">{accountability.jobCompletionRate || 0}%</div>
            <ProgressBar value={accountability.jobCompletionRate || 0} color="purple" />
          </div>
        </div>

        {/* Activity Stats */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">User Activity</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Active This Week</span>
                  <span className="font-medium">{accountability.activeUsersThisWeek || 0}</span>
                </div>
                <ProgressBar value={accountability.activeUsersThisWeek || 0} max={100} color="green" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Active This Month</span>
                  <span className="font-medium">{accountability.activeUsersThisMonth || 0}</span>
                </div>
                <ProgressBar value={accountability.activeUsersThisMonth || 0} max={100} color="blue" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Platform Health</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Uptime</span>
                <span className="font-semibold text-green-600">{accountability.uptimePercentage || 99.9}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Feedback Received</span>
                <span className="font-semibold">{accountability.totalFeedbackReceived || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Avg Rating</span>
                <span className="font-semibold">{(accountability.avgRating || 0).toFixed(1)} / 5.0</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const UserGrowthTab = () => {
    if (growthLoading) {
      return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
    }

    return (
      <>
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">New Users Over Time</h3>
            <SimpleBarChart 
              data={userGrowth} 
              dataKey="newUsers" 
              labelKey="date" 
              height={250}
              color="green"
            />
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Cumulative User Growth</h3>
            <SimpleBarChart 
              data={userGrowth} 
              dataKey="cumulativeUsers" 
              labelKey="date" 
              height={250}
              color="blue"
            />
          </div>
        </div>
      </>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  if (overviewLoading && activeTab === 'overview') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'tokens', label: 'Token Health', icon: Coins },
    { id: 'tiers', label: 'Tier Analytics', icon: Crown },
    { id: 'accountability', label: 'Platform Accountability', icon: Shield },
    { id: 'growth', label: 'User Growth', icon: Users }
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Platform Analytics</h1>
            <p className="text-gray-600">Real-time platform performance metrics</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRefresh}
              className="p-2 hover:bg-gray-100 rounded-lg bg-white border"
            >
              <RefreshCw size={20} />
            </button>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(parseInt(e.target.value))}
              className="px-4 py-2 border rounded-lg bg-white"
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6 border">
        <div className="flex border-b">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <TabIcon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'tokens' && <TokenHealthTab />}
      {activeTab === 'tiers' && <TierAnalyticsTab />}
      {activeTab === 'accountability' && <PlatformAccountabilityTab />}
      {activeTab === 'growth' && <UserGrowthTab />}
    </div>
  );
};

export default AdminAnalytics;